import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { getPlanDisplayName } from '../../lib/plans';
import { STATE_LABELS, WORKSPACE_FIELDS } from '../../lib/workspaces';
import { PLAN_PRICES } from '../../lib/plans';
import type { Profile, Workspace } from '../../types/database';

const fullName = (p: Pick<Profile, 'first_name' | 'last_name'>) =>
  [p.first_name, p.last_name].filter(Boolean).join(' ') || '(no name)';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'None');

type Member = {
  role: 'owner' | 'admin' | 'member';
  profiles: Pick<Profile, 'id' | 'email' | 'first_name' | 'last_name'> | null;
};

// A Workspace's members, state and billing, read with the Staff's own session. The Staff
// read policies make this work for any Workspace, Locked ones included.
export function StaffWorkspace() {
  const { id } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['staff-workspace', id],
    queryFn: async () => {
      const [workspace, members] = await Promise.all([
        supabase.from('workspaces').select(WORKSPACE_FIELDS).eq('id', id!).maybeSingle(),
        supabase
          .from('workspace_members')
          .select('role, profiles:user_id(id, email, first_name, last_name)')
          .eq('workspace_id', id!)
          .order('created_at'),
      ]);
      if (workspace.error) throw workspace.error;
      if (members.error) throw members.error;

      // Other Workspaces tracking the same brand. brand_url is stored normalised at onboarding.
      const brandUrl = (workspace.data as Workspace | null)?.brand_url;
      // And Workspaces tracking it as a competitor. Domains are stored normalised too.
      const [sameBrand, asCompetitor] = brandUrl
        ? await Promise.all([
            supabase
              .from('workspaces')
              .select('id, name, state')
              .eq('brand_url', brandUrl)
              .neq('id', id!)
              .order('created_at')
              .limit(50),
            supabase
              .from('competitors')
              .select('workspaces:workspace_id(id, name, brand_url, state)')
              .in('domain', [brandUrl, `www.${brandUrl}`])
              .neq('workspace_id', id!)
              .limit(50),
          ])
        : [null, null];
      if (sameBrand?.error) throw sameBrand.error;
      if (asCompetitor?.error) throw asCompetitor.error;
      const trackers = new Map(
        (
          (asCompetitor?.data ?? []) as unknown as {
            workspaces: Pick<Workspace, 'id' | 'name' | 'brand_url' | 'state'> | null;
          }[]
        ).flatMap((c) => (c.workspaces ? [[c.workspaces.id, c.workspaces] as const] : []))
      );

      return {
        workspace: workspace.data as Workspace | null,
        members: members.data as unknown as Member[],
        sameBrand: (sameBrand?.data ?? []) as unknown as Pick<Workspace, 'id' | 'name' | 'state'>[],
        trackers: [...trackers.values()],
      };
    },
  });

  if (isLoading) return <div className="loading-spinner" />;
  if (error) return <p className="text-size-small staff-error">{error.message}</p>;
  if (!data?.workspace) return <p className="text-size-small text-colour-alt-grey">No Workspace with this id.</p>;

  const { workspace: w, members, sameBrand, trackers } = data;
  const owner = members.find((m) => m.role === 'owner')?.profiles;
  const pending = [w.pending_plan && getPlanDisplayName(w.pending_plan), w.pending_billing_interval]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="staff-detail">
      <h1 className="text-size-large">{w.name}</h1>
      <dl className="staff-fields">
        <dt>Brand</dt>
        <dd>{[w.brand_name, w.brand_url].filter(Boolean).join(' · ') || 'None'}</dd>
        <dt>Owner</dt>
        <dd>{owner ? <Link to={`/staff/users/${owner.id}`}>{owner.email ?? fullName(owner)}</Link> : 'None'}</dd>
        <dt>State</dt>
        <dd>{w.state ? STATE_LABELS[w.state] : 'Unknown'}</dd>
        <dt>Plan</dt>
        <dd>
          {getPlanDisplayName(w.plan)}
          {w.billing_interval && `, ${w.billing_interval}`}
        </dd>
        <dt>Limits</dt>
        <dd>
          {w.prompt_limit} prompts, {w.team_member_limit ?? 'unlimited'} team members
        </dd>
        <dt>Subscription</dt>
        <dd>
          {w.stripe_subscription_id ? (
            <a
              href={`https://dashboard.stripe.com/subscriptions/${w.stripe_subscription_id}`}
              target="_blank"
              rel="noreferrer"
            >
              {w.stripe_subscription_id}
            </a>
          ) : (
            'None'
          )}
        </dd>
        <dt>Subscription status</dt>
        <dd>{w.subscription_status ?? 'None'}</dd>
        <dt>Trial end date</dt>
        <dd>{formatDate(w.trial_ends_at)}</dd>
        <dt>Pending billing change</dt>
        <dd>{pending || 'None'}</dd>
        {w.comped_plan && (
          <>
            <dt>Comped plan</dt>
            <dd>
              {getPlanDisplayName(w.comped_plan)}
              {!w.comped && ' (not in force)'}
            </dd>
            <dt>Comped until</dt>
            <dd>{w.comped_until ? formatDate(w.comped_until) : 'No end date'}</dd>
            <dt>Comped reason</dt>
            <dd>{w.comped_reason || 'None'}</dd>
          </>
        )}
        <dt>Created</dt>
        <dd>{formatDate(w.created_at)}</dd>
      </dl>

      <div className="staff-results">
        <Comp workspace={w} />
        <section>
          <h2 className="text-size-medium text-weight-semibold">Members</h2>
          {members.length === 0 && <p className="text-size-small text-colour-alt-grey">No members.</p>}
          {members.map(
            (m) =>
              m.profiles && (
                <Link key={m.profiles.id} to={`/staff/users/${m.profiles.id}`} className="staff-result">
                  <span>
                    {fullName(m.profiles)} <span className="text-colour-alt-grey">{m.profiles.email}</span>
                  </span>
                  <span className="text-colour-alt-grey">{m.role}</span>
                </Link>
              )
          )}
        </section>
        {w.brand_url && (
          <section>
            <h2 className="text-size-medium text-weight-semibold">Other Workspaces for {w.brand_url}</h2>
            {sameBrand.length === 0 && <p className="text-size-small text-colour-alt-grey">None.</p>}
            {sameBrand.map((o) => (
              <Link key={o.id} to={`/staff/workspaces/${o.id}`} className="staff-result">
                <span>{o.name}</span>
                <span className="text-colour-alt-grey">{o.state ? STATE_LABELS[o.state] : 'Unknown'}</span>
              </Link>
            ))}
          </section>
        )}
        {w.brand_url && (
          <section>
            <h2 className="text-size-medium text-weight-semibold">Workspaces tracking {w.brand_url} as a competitor</h2>
            {trackers.length === 0 && <p className="text-size-small text-colour-alt-grey">None.</p>}
            {trackers.map((o) => (
              <Link key={o.id} to={`/staff/workspaces/${o.id}`} className="staff-result">
                <span>
                  {o.name} <span className="text-colour-alt-grey">{o.brand_url}</span>
                </span>
                <span className="text-colour-alt-grey">{o.state ? STATE_LABELS[o.state] : 'Unknown'}</span>
              </Link>
            ))}
          </section>
        )}
      </div>
      <Link to={`/staff/activity?workspace=${w.id}`} className="small-link">Staff activity on this Workspace</Link>
    </div>
  );
}

type Plan = keyof typeof PLAN_PRICES;

// Comp a Workspace with no active Subscription onto a plan, or end a comp in force. Both are
// Postgres functions that check verified Staff and write the activity log themselves.
function Comp({ workspace: w }: { workspace: Workspace }) {
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState<Plan>('growth');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (comp: boolean) => {
      const { error } = comp
        ? await supabase.rpc('comp_workspace', {
            workspace: w.id,
            plan,
            // Paid through the end of the chosen day, in the Staff's time zone.
            until: until ? new Date(`${until}T23:59:59.999`).toISOString() : null,
            reason: reason.trim(),
          })
        : await supabase.rpc('uncomp_workspace', { workspace: w.id });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff-workspace', w.id] }),
  });

  // Paid without a comp means Paid through a Subscription, which comp_workspace refuses.
  if (w.state === 'paid' && !w.comped) return null;

  return (
    <section>
      <h2 className="text-size-medium text-weight-semibold">Comp</h2>
      {w.comped ? (
        <button className="button is-small w-button" disabled={isPending} onClick={() => mutate(false)}>
          {isPending ? 'Ending...' : 'End comp'}
        </button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutate(true);
          }}
        >
          <label htmlFor="comp-plan" className="form_label">Plan</label>
          <select
            id="comp-plan"
            className="form_input is-light w-input"
            value={plan}
            onChange={(e) => setPlan(e.target.value as Plan)}
          >
            {(Object.keys(PLAN_PRICES) as Plan[]).map((p) => (
              <option key={p} value={p}>{getPlanDisplayName(p)}</option>
            ))}
          </select>
          <label htmlFor="comp-until" className="form_label">Until (optional)</label>
          <input
            id="comp-until"
            type="date"
            className="form_input is-light w-input"
            value={until}
            min={new Date().toLocaleDateString('en-CA')}
            onChange={(e) => setUntil(e.target.value)}
          />
          <label htmlFor="comp-reason" className="form_label">Reason</label>
          <input
            id="comp-reason"
            className="form_input is-light w-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          <button type="submit" className="button is-small w-button" disabled={isPending}>
            {isPending ? 'Comping...' : 'Comp'}
          </button>
        </form>
      )}
      {error && <p className="text-size-small staff-error">{error.message}</p>}
    </section>
  );
}
